import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { SpotifySearchSection } from './SpotifySearchSection';
import { FileUploadSection } from './FileUploadSection';
import LayoutPhotoUpload, { LayoutPhotoFile } from '@/components/sequences/upload/LayoutPhotoUpload';
import { layoutPhotoService } from '@/services/layoutPhotoService';
import { useAdminSequences, SequenceWithMetadata, UserWithSequences } from '@/hooks/useAdminSequences';
import { spotifyService, SpotifyTrack } from '@/services/spotifyService';
import enhancedSpotifyService from '@/services/enhancedSpotifyService';
import { toast } from 'sonner';
import { HOLIDAY_OPTIONS } from '@/constants/holidays';
import { Plus, Save, RefreshCw, Music, Upload, AlertTriangle, CheckCircle, ExternalLink, Check, ChevronsUpDown, Trash2, MoreVertical, Search, TestTube, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

interface SequenceEditorProps {
  preselectedSequenceId?: string | null;
}

export function SequenceEditor({ preselectedSequenceId }: SequenceEditorProps) {
  const {
    sequences,
    artists,
    genres,
    loading,
    fetchArtists,
    fetchGenres,
    createSequence,
    updateSequence,
    fetchUsersWithSequences,
    findArtistByName,
    createArtist,
    createGenre,
    uploadSequenceFile,
    findDuplicateSequences,
    deleteSequence
  } = useAdminSequences();

  const [selectedSequenceId, setSelectedSequenceId] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<SequenceWithMetadata>>({});
  const [spotifyData, setSpotifyData] = useState<SpotifyTrack | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingArtist, setPendingArtist] = useState<string | null>(null);
  const [pendingGenre, setPendingGenre] = useState<string | null>(null);
  const [artistOpen, setArtistOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);
  const [layoutPhotos, setLayoutPhotos] = useState<LayoutPhotoFile[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [sequenceOwners, setSequenceOwners] = useState<UserWithSequences[]>([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const justSavedRef = useRef(false);

  // Track if form has been initialized for the current sequence
  const initializedSequenceRef = useRef<string | null>(null);

  // Fetch sequence owners when component mounts or when needed
  const loadSequenceOwners = async () => {
    // Check if hook has fully loaded and function exists
    if (!fetchUsersWithSequences || typeof fetchUsersWithSequences !== 'function') {
      console.warn('fetchUsersWithSequences not ready yet, skipping...');
      return;
    }
    
    setLoadingOwners(true);
    try {
      const owners = await fetchUsersWithSequences();
      setSequenceOwners(owners);
    } catch (error) {
      console.error('Failed to fetch sequence owners:', error);
      toast.error('Failed to load sequence owners');
    } finally {
      setLoadingOwners(false);
    }
  };

  // Load sequence owners when hook is ready (only once when loading completes)
  useEffect(() => {
    if (fetchUsersWithSequences && !loading && sequenceOwners.length === 0) {
      loadSequenceOwners();
    }
  }, [loading]); // Removed fetchUsersWithSequences from deps to prevent infinite loop

  // Update form data when sequences list changes and we have a selected sequence
  // BUT NOT during save operations or immediately after to avoid overriding user changes
  useEffect(() => {
    if (selectedSequenceId && !isCreating && !saving && !justSavedRef.current) {
      const sequence = sequences.find(s => s.id === selectedSequenceId);
      if (sequence) {
        // Only sync form data if:
        // 1. We haven't initialized this sequence yet, OR
        // 2. The sequence data has been updated from the server (after save)
        const shouldSync = 
          initializedSequenceRef.current !== selectedSequenceId ||
          (justSavedRef.current === false && initializedSequenceRef.current === selectedSequenceId);
        
        if (shouldSync) {
          console.log('Syncing form with DB values for sequence:', selectedSequenceId);
          setFormData(sequence);
          initializedSequenceRef.current = selectedSequenceId;

          // Load layout photos for this sequence
          loadLayoutPhotos(selectedSequenceId);
        }
      }
    }
    
    // Reset the justSaved flag after a delay to allow for future syncing
    if (justSavedRef.current) {
      const timer = setTimeout(() => {
        justSavedRef.current = false;
      }, 2000); // Wait 2 seconds before allowing form sync again
      
      return () => clearTimeout(timer);
    }
  }, [sequences, selectedSequenceId, isCreating, saving]);

  // Reset form when switching sequences
  const handleSequenceSelect = useCallback((sequenceId: string) => {
    if (sequenceId === 'new') {
      setIsCreating(true);
      setSelectedSequenceId('');
      setFormData({
        name: '',
        title: '',
        description: '',
        price: 0,
        is_free: true,
        software_type: 'xLights'
      });
      setSpotifyData(null);
      setLayoutPhotos([]); // Clear photos when creating new sequence
      initializedSequenceRef.current = null; // Reset initialization tracking
    } else {
      setIsCreating(false);
      setSelectedSequenceId(sequenceId);
      const sequence = sequences.find(s => s.id === sequenceId);
      if (sequence) {
        setFormData(sequence);
        initializedSequenceRef.current = sequenceId; // Mark as initialized

        // Load layout photos for this sequence
        loadLayoutPhotos(sequenceId);
      }
      setSpotifyData(null);
    }
    setPendingArtist(null);
    setPendingGenre(null);
  }, [sequences]);

  // Handle preselected sequence from URL parameter
  useEffect(() => {
    if (preselectedSequenceId && sequences.length > 0 && !selectedSequenceId) {
      const sequence = sequences.find(s => s.id === preselectedSequenceId);
      if (sequence) {
        console.log('Auto-selecting sequence from URL parameter:', preselectedSequenceId);
        handleSequenceSelect(preselectedSequenceId);
      } else {
        console.warn('Preselected sequence not found:', preselectedSequenceId);
      }
    }
  }, [preselectedSequenceId, sequences, selectedSequenceId, handleSequenceSelect]);

  // Load layout photos for a sequence
  const loadLayoutPhotos = async (sequenceId: string) => {
    try {
      setPhotosLoading(true);
      const existingPhotos = await layoutPhotoService.getLayoutPhotos(sequenceId);
      const photosWithPreviews: LayoutPhotoFile[] = existingPhotos.map((photo, index) => ({
        file: new File([], `photo-${index}`, { type: 'image/jpeg' }), // Placeholder file
        preview: photo.photo_url,
        caption: photo.caption || '',
        id: photo.id,
      }));
      setLayoutPhotos(photosWithPreviews);
    } catch (photoError) {
      console.error('Error loading layout photos:', photoError);
      toast.error('Failed to load layout photos');
      setLayoutPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  // Handle layout photo changes
  const handleLayoutPhotosChange = (photos: LayoutPhotoFile[]) => {
    setLayoutPhotos(photos);
  };

  // Save layout photos for a sequence
  const saveLayoutPhotos = async (sequenceId: string) => {
    // Get existing photos to compare
    const existingPhotos = await layoutPhotoService.getLayoutPhotos(sequenceId);
    const existingPhotoIds = new Set(existingPhotos.map(p => p.id));

    // Find new photos that need to be uploaded
    const newPhotos = layoutPhotos.filter(photo =>
      !existingPhotoIds.has(photo.id) && photo.file.size > 0
    );

    // Upload new photos
    if (newPhotos.length > 0) {
      const photosToUpload = newPhotos.map(photo => ({
        file: photo.file,
        caption: photo.caption
      }));
      await layoutPhotoService.uploadLayoutPhotos(sequenceId, photosToUpload);
    }

    // Update captions for existing photos
    for (const photo of layoutPhotos) {
      if (existingPhotoIds.has(photo.id)) {
        const existingPhoto = existingPhotos.find(p => p.id === photo.id);
        if (existingPhoto && existingPhoto.caption !== photo.caption) {
          await layoutPhotoService.updateCaption(photo.id, photo.caption || '');
        }
      }
    }

    // Remove photos that were deleted
    const currentPhotoIds = new Set(layoutPhotos.map(p => p.id));
    for (const existingPhoto of existingPhotos) {
      if (!currentPhotoIds.has(existingPhoto.id)) {
        await layoutPhotoService.deleteLayoutPhoto(existingPhoto.id);
      }
    }
  };

  // Handle Spotify track selection
  const handleSpotifySelect = async (track: SpotifyTrack) => {
    setSpotifyData(track);
    
    // Auto-populate fields from Spotify
    const albumCoverUrl = track.album.images.find(img => img.width === 640)?.url || track.album.images[0]?.url;
    const updates = {
      ...formData,
      title: `${track.name} - ${track.artists[0]?.name}`,
      album: track.album.name,
      image_url: albumCoverUrl,
      album_cover_url: albumCoverUrl,
      spotify_track_id: track.id,
      spotify_explicit: track.explicit,
      spotify_preview_url: track.preview_url,
      spotify_duration_ms: track.duration_ms,
      spotify_popularity: track.popularity,
      duration: Math.round(track.duration_ms / 1000)
    };

    let createdNewArtist = false;
    let newArtistData = null;

    // Check if artist exists using database search, auto-create if not found
    const artistName = track.artists[0]?.name;
    if (artistName) {
      console.log(`Searching for existing artist: "${artistName}"`);
      
      try {
        const existingArtist = await findArtistByName(artistName);
        
        if (existingArtist) {
          updates.artist_id = existingArtist.id;
          console.log(`Found existing artist: "${existingArtist.name}" (${existingArtist.id}) for Spotify artist: "${artistName}"`);
        } else {
          console.log(`No existing artist found for: "${artistName}". Auto-creating artist from Spotify data...`);
          
          try {
            // Show loading state
            toast.loading(`Creating artist "${artistName}"...`, { id: 'creating-artist' });
            
            // Prepare artist data from Spotify track
            const artistDescription = `Spotify artist for "${track.name}"`;
            const artistImageUrl = track.album.images.find(img => img.width >= 300)?.url || 
                                 track.album.images[0]?.url;
            
            // Automatically create the artist (createArtist already calls fetchArtists internally)
            const createdArtist = await createArtist(artistName, artistDescription, artistImageUrl);
            
            updates.artist_id = createdArtist.id;
            createdNewArtist = true;
            newArtistData = createdArtist;
            
            // Success feedback
            toast.success(`Artist "${createdArtist.name}" created successfully`, { id: 'creating-artist' });
            console.log(`Auto-created artist: "${createdArtist.name}" (${createdArtist.id}) from Spotify data`);
            
          } catch (createError) {
            console.error(`Failed to auto-create artist "${artistName}":`, createError);
            
            // Fallback to manual creation on error
            toast.error(`Failed to create artist "${artistName}". Please create manually.`, { id: 'creating-artist' });
            setPendingArtist(artistName);
            console.log(`Auto-creation failed for: "${artistName}". Falling back to manual creation.`);
          }
        }
      } catch (searchError) {
        console.error(`Error searching for artist "${artistName}":`, searchError);
        
        // Fallback to manual creation on search error
        toast.warning(`Could not search for artist "${artistName}". Please create manually.`);
        setPendingArtist(artistName);
        console.log(`Artist search failed for: "${artistName}". Falling back to manual creation.`);
      }
    }

    // Extract and process genres from Spotify
    try {
      const genreSuggestions = await enhancedSpotifyService.getGenreSuggestions(track);
      if (genreSuggestions && genreSuggestions.length > 0) {
        const genreNames = genreSuggestions.slice(0, 6).map(g => g.name || g.slug).filter(Boolean);
        
        // Auto-populate genres in form data
        updates.genres = genreNames;
        updates.primary_genre = genreNames[0] || null;
        updates.secondary_genre = genreNames[1] || null;
        
        console.log('Extracted genres from Spotify:', genreNames);
      }
    } catch (genreError) {
      console.warn('Failed to extract genres from Spotify:', genreError);
    }

    setFormData(updates);
    
    // Enhanced dropdown refresh with better timing and debugging
    if (createdNewArtist && newArtistData) {
      try {
        console.log('=== DROPDOWN REFRESH DEBUG ===');
        console.log('Created new artist, refreshing dropdowns...', newArtistData);
        
        toast.loading('Ensuring dropdowns are up to date...', { id: 'refresh-data' });
        
        // Small delay to ensure database transaction is fully committed
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Log current artists state before refresh
        console.log('Artists before refresh:', artists.length, artists.map(a => a.name));
        
        // Since createArtist already calls fetchArtists, just call fetchGenres for genres
        // But also call fetchArtists again with a delay to ensure consistency
        await Promise.all([
          fetchArtists(),
          fetchGenres()
        ]);
        
        // Log artists state after refresh
        console.log('Artists after refresh:', artists.length, artists.map(a => a.name));
        
        // Verify the new artist is in the list
        const refreshedArtist = artists.find(a => a.id === newArtistData.id || a.name === newArtistData.name);
        if (refreshedArtist) {
          console.log('✅ New artist found in refreshed list:', refreshedArtist);
          toast.success('Dropdowns updated successfully', { id: 'refresh-data' });
        } else {
          console.warn('⚠️ New artist not found in refreshed list. Forcing another refresh...');
          // Try one more time with a longer delay
          await new Promise(resolve => setTimeout(resolve, 300));
          await fetchArtists();
          
          const secondRefreshCheck = artists.find(a => a.id === newArtistData.id || a.name === newArtistData.name);
          if (secondRefreshCheck) {
            console.log('✅ New artist found after second refresh:', secondRefreshCheck);
            toast.success('Dropdowns updated successfully', { id: 'refresh-data' });
          } else {
            console.error('❌ New artist still not found after multiple refreshes');
            toast.warning('Dropdown may need manual refresh', { id: 'refresh-data' });
          }
        }
        
        console.log('=== END DROPDOWN REFRESH DEBUG ===');
        
      } catch (refreshError) {
        console.error('Failed to refresh dropdown data:', refreshError);
        toast.error('Failed to refresh dropdowns', { id: 'refresh-data' });
      }
    } else if (artistName) {
      // If no new artist was created but we have an artist, just refresh genres
      try {
        await fetchGenres();
        toast.success('Genres updated', { id: 'refresh-data' });
      } catch (refreshError) {
        console.error('Failed to refresh genres:', refreshError);
      }
    }
  };


  // Handle form field changes
  const handleFieldChange = (field: string, value: unknown) => {
    // Convert empty strings to null for optional fields
    const processedValue = (typeof value === 'string' && value.trim() === '') ? null : value;
    
    console.log(`Field changed: ${field} = "${value}" -> processed: ${processedValue}`);
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  // Debug function to test direct Supabase update
  const testDirectUpdate = async () => {
    if (!selectedSequenceId) return;
    
    try {
      console.log('Testing direct Supabase update...');
      
      // Test 1: Check current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('Current authenticated user:', user?.id, userError);
      
      // Test 2: Try direct update
      const { data, error } = await supabase
        .from('sequences')
        .update({ 
          description: 'Direct test update - ' + new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSequenceId)
        .select();
      
      console.log('Direct update result:', { data, error });
      
      if (error) {
        console.error('Direct update failed:', error);
        alert(`Update failed: ${error.message}`);
      } else {
        console.log('Direct update succeeded:', data);
        alert('Direct update succeeded! Check console for details.');
      }
    } catch (err) {
      console.error('Test failed:', err);
      alert(`Test failed: ${err}`);
    }
  };

  // Check for duplicate sequences
  const handleCheckDuplicates = async () => {
    try {
      const duplicates = await findDuplicateSequences();
      if (duplicates.length > 0) {
        const message = duplicates.map(d => `"${d.title}" (${d.sequences.length} copies)`).join('\n');
        alert(`Found ${duplicates.length} sets of duplicate sequences:\n\n${message}`);
      } else {
        alert('No duplicate sequences found!');
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast.error('Failed to check for duplicates');
    }
  };

  // Create pending artist (fallback for when auto-creation fails)
  const handleCreateArtist = async () => {
    if (!pendingArtist) return;
    
    try {
      console.log(`Manually creating artist: "${pendingArtist}"`);
      
      // Show loading state
      toast.loading(`Creating artist "${pendingArtist}"...`, { id: 'manual-creating-artist' });
      
      // Use Spotify data if available, otherwise use basic description
      const artistDescription = spotifyData 
        ? `Spotify artist for "${spotifyData.name}"`
        : `Artist for ${pendingArtist}`;
      
      // Use higher quality image if available
      const artistImageUrl = spotifyData?.album.images.find(img => img.width >= 300)?.url || 
                             spotifyData?.album.images[0]?.url;
      
      const artist = await createArtist(pendingArtist, artistDescription, artistImageUrl);
      
      setFormData(prev => ({
        ...prev,
        artist_id: artist.id
      }));
      
      setPendingArtist(null);
      
      // Success feedback
      toast.success(`Artist "${artist.name}" created successfully`, { id: 'manual-creating-artist' });
      console.log(`Manual artist creation completed successfully:`, artist);
      
    } catch (error: any) {
      console.error('Error creating artist:', error);
      toast.error(`Failed to create artist: ${error?.message || 'Unknown error'}`, { id: 'manual-creating-artist' });
    }
  };

  // Create pending genre
  const handleCreateGenre = async () => {
    if (!pendingGenre) return;
    
    try {
      const genre = await createGenre(pendingGenre);
      
      setFormData(prev => ({
        ...prev,
        genre_id: genre.id
      }));
      
      setPendingGenre(null);
      toast.success(`Genre "${pendingGenre}" created successfully`);
    } catch (error) {
      toast.error('Failed to create genre');
      console.error('Error creating genre:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    if (!selectedSequenceId && !isCreating) {
      toast.error('Please select or create a sequence first');
      return;
    }

    try {
      const sequenceId = selectedSequenceId || 'temp-' + Date.now();
      const result = await uploadSequenceFile(file, sequenceId);

      setFormData(prev => ({
        ...prev,
        storage_path: (result as any).path,
        file_url: (result as any).path
      }));
      
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
      console.error('Error uploading file:', error);
    }
  };

  // Handle sequence deletion
  const handleDelete = async () => {
    if (!selectedSequenceId) return;

    setDeleting(true);
    try {
      await deleteSequence(selectedSequenceId);
      
      toast.success('Sequence deleted successfully');
      setShowDeleteDialog(false);
      setSelectedSequenceId('');
      setFormData({});
      setIsCreating(false);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete sequence');
    } finally {
      setDeleting(false);
    }
  };

  // Save sequence
  const handleSave = async () => {
    console.log('=== SAVE BUTTON CLICKED ===');
    console.log('Form data being saved:', formData);
    console.log('Selected sequence ID:', selectedSequenceId);
    console.log('Is creating:', isCreating);
    
    if (!formData.name || !formData.title) {
      toast.error('Name and title are required');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        console.log('Creating new sequence...');
        // Create new sequence
        const newSequence = await createSequence(formData);
        console.log('New sequence created:', newSequence);

        // Handle layout photos for new sequence
        if (layoutPhotos.length > 0) {
          try {
            await saveLayoutPhotos(newSequence.id);
          } catch (photoError) {
            console.error('Error saving layout photos for new sequence:', photoError);
            toast.error('Failed to save layout photos');
            return; // Don't continue if photo save fails
          }
        }

        toast.success('Sequence created successfully');

        // Switch to editing the newly created sequence
        setIsCreating(false);
        setSelectedSequenceId(newSequence.id);
        justSavedRef.current = true; // Prevent form data override after creation
      } else if (selectedSequenceId) {
        console.log('Updating existing sequence...');
        // Update existing sequence
        const result = await updateSequence(selectedSequenceId, formData);
        console.log('Sequence update result:', result);

        // Handle layout photos for updated sequence
        try {
          await saveLayoutPhotos(selectedSequenceId);
        } catch (photoError) {
          console.error('Error saving layout photos:', photoError);
          toast.error('Failed to save layout photos');
          return; // Don't continue if photo save fails
        }

        toast.success('Sequence updated successfully');

        justSavedRef.current = true; // Prevent form data override after update
      }
    } catch (error) {
      toast.error('Failed to save sequence');
      console.error('Error saving sequence:', error);
    } finally {
      setSaving(false);
      console.log('=== SAVE COMPLETED ===');
    }
  };

  const selectedSequence = sequences.find(s => s.id === selectedSequenceId);

  return (
    <div className="space-y-6">
      {/* Sequence Selector */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="sequence-select" className="text-base font-medium">
            Select Sequence to Edit
          </Label>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {sequences.length} sequences available
            </Badge>
            
            {/* Advanced Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">Advanced tools</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal">
                  Advanced Tools
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <DropdownMenuItem 
                  onClick={handleCheckDuplicates}
                  className="cursor-pointer"
                >
                  <Search className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>Check Duplicates</span>
                    <span className="text-xs text-muted-foreground">
                      Find duplicate sequences
                    </span>
                  </div>
                </DropdownMenuItem>
                
                {selectedSequenceId && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={testDirectUpdate}
                      className="cursor-pointer text-red-600 focus:text-red-600"
                    >
                      <TestTube className="mr-2 h-4 w-4" />
                      <div className="flex flex-col">
                        <span>Test Update</span>
                        <span className="text-xs text-muted-foreground">
                          Debug permissions
                        </span>
                      </div>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <Popover open={sequenceOpen} onOpenChange={setSequenceOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={sequenceOpen}
              className="w-full justify-between"
            >
              {selectedSequenceId 
                ? sequences.find(s => s.id === selectedSequenceId)?.title || sequences.find(s => s.id === selectedSequenceId)?.name
                : isCreating
                  ? "Create New Sequence"
                  : "Select a sequence or create new..."
              }
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput placeholder="Search sequences..." />
              <CommandEmpty>No sequence found.</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  <CommandItem
                    value="new"
                    onSelect={() => {
                      handleSequenceSelect('new');
                      setSequenceOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isCreating ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Sequence
                  </CommandItem>
                  {sequences
                    .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name))
                    .map((sequence) => (
                    <CommandItem
                      key={sequence.id}
                      value={`${sequence.title || sequence.name} ${sequence.name} ${sequence.id.slice(0, 8)}`}
                      onSelect={() => {
                        handleSequenceSelect(sequence.id);
                        setSequenceOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedSequenceId === sequence.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="truncate">{sequence.title || sequence.name}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            ID: {sequence.id.slice(0, 8)}... • Created: {new Date(sequence.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex gap-1 ml-2 shrink-0">
                          {sequence.spotify_track_id && (
                            <Badge variant="secondary" className="text-xs">S</Badge>
                          )}
                          {sequence.artist_id && (
                            <Badge variant="secondary" className="text-xs">A</Badge>
                          )}
                          {sequence.genre_id && (
                            <Badge variant="secondary" className="text-xs">G</Badge>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Preview Button - only show when editing existing sequence */}
      {selectedSequenceId && !isCreating && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(`/sequence/${selectedSequenceId}`, '_blank')}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Preview on Site
          </Button>
        </div>
      )}

      {(selectedSequenceId || isCreating) && (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Form Fields */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Sequence Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      placeholder="Sequence identifier"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title || ''}
                      onChange={(e) => handleFieldChange('title', e.target.value)}
                      placeholder="Display title"
                    />
                  </div>
                </div>

                {/* Sequence Owner - Admin Only */}
                {selectedSequenceId && !isCreating && (
                  <div>
                    <Label htmlFor="owner">Sequence Owner</Label>
                    <Popover open={ownerOpen} onOpenChange={setOwnerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={ownerOpen}
                          className="w-full justify-between"
                          onClick={() => {
                            if (sequenceOwners.length === 0) {
                              loadSequenceOwners();
                            }
                          }}
                        >
                          {formData.user_id && sequenceOwners.length > 0 ? (
                            (() => {
                              const owner = sequenceOwners.find(o => o.user_id === formData.user_id);
                              const displayName = owner?.display_name || owner?.username || owner?.full_name || 'Unknown User';
                              return `${displayName} (${owner?.sequence_count || 0} sequences)`;
                            })()
                          ) : loadingOwners ? (
                            "Loading owners..."
                          ) : (
                            "Select owner..."
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search by name..." />
                          <CommandEmpty>
                            {loadingOwners ? "Loading..." : "No users found."}
                          </CommandEmpty>
                          <CommandList>
                            <CommandGroup>
                              {sequenceOwners.map((owner) => {
                                const displayName = owner.display_name || owner.username || owner.full_name || 'Unknown User';
                                return (
                                  <CommandItem
                                    key={owner.user_id}
                                    value={`${displayName} ${owner.username || ''} ${owner.user_id}`}
                                    onSelect={() => {
                                      handleFieldChange('user_id', owner.user_id);
                                      setOwnerOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        formData.user_id === owner.user_id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex justify-between items-center w-full">
                                      <span>{displayName}</span>
                                      <Badge variant="outline" className="ml-2">
                                        {owner.sequence_count} {owner.sequence_count === 1 ? 'sequence' : 'sequences'}
                                      </Badge>
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}


                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Sequence description"
                    rows={3}
                  />
                </div>

                {/* Artist and Genre */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="artist">Artist</Label>
                    {pendingArtist ? (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Artist "{pendingArtist}" doesn't exist.
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2"
                            onClick={handleCreateArtist}
                          >
                            Create Artist
                          </Button>
                        </AlertDescription>
                      </Alert>
                    ) : (
                                              <Popover open={artistOpen} onOpenChange={setArtistOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={artistOpen}
                              className="w-full justify-between"
                            >
                              {formData.artist_id 
                                ? artists.find(a => a.id === formData.artist_id)?.name 
                                : "Select artist..."
                              }
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-full p-0">
                            <Command>
                              <CommandInput placeholder="Search artist..." />
                              <CommandEmpty>No artist found.</CommandEmpty>
                              <CommandList>
                                <CommandGroup>
                                  {artists
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((artist) => (
                                    <CommandItem
                                      key={artist.id}
                                      value={artist.name}
                                      onSelect={() => {
                                        handleFieldChange('artist_id', artist.id);
                                        setArtistOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          formData.artist_id === artist.id ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                      {artist.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="genre">Genres (up to 6)</Label>
                    <Popover open={genreOpen} onOpenChange={setGenreOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={genreOpen}
                          className="w-full justify-between"
                        >
                          {Array.isArray(formData.genres) && formData.genres.length > 0
                            ? `${formData.genres.join(', ')}`
                            : "Select genres..."
                          }
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search genre..." />
                          <CommandEmpty>No genre found.</CommandEmpty>
                          <CommandList>
                            <CommandGroup>
                          {genres
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((genre) => (
                            <CommandItem
                              key={genre.id}
                              value={genre.name}
                              onSelect={() => {
                                const current = Array.isArray(formData.genres) ? formData.genres : [];
                                const exists = current.includes(genre.slug || genre.name);
                                const next = exists
                                  ? current.filter(g => g !== (genre.slug || genre.name))
                                  : current.length < 6
                                    ? [...current, (genre.slug || genre.name)]
                                    : current;
                                setFormData(prev => ({ ...prev, genres: next }));
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  (Array.isArray(formData.genres) && formData.genres.includes(genre.slug || genre.name)) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {genre.name}
                            </CommandItem>
                          ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Pricing and Software */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="price">Price ($)</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price || 0}
                      onChange={(e) => handleFieldChange('price', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration">Duration (sec)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.duration || ''}
                      onChange={(e) => handleFieldChange('duration', parseInt(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="software">Software</Label>
                    <Select 
                      value={formData.software_type || undefined} 
                      onValueChange={(value) => handleFieldChange('software_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select software..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xLights">xLights</SelectItem>
                        <SelectItem value="Light-O-Rama">Light-O-Rama</SelectItem>
                        <SelectItem value="Vixen">Vixen</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Holiday and File Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="holiday_type">Holiday</Label>
                    <Select 
                      value={formData.holiday_type || undefined} 
                      onValueChange={(value) => handleFieldChange('holiday_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select holiday..." />
                      </SelectTrigger>
                      <SelectContent>
                        {HOLIDAY_OPTIONS.map((holiday) => (
                          <SelectItem key={holiday.value} value={holiday.value}>
                            <span className="flex items-center gap-2">
                              <span>{holiday.icon}</span>
                              <span>{holiday.label}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="file_type">File Type</Label>
                    <Select 
                      value={formData.file_type || undefined} 
                      onValueChange={(value) => handleFieldChange('file_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select file type..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sequence only">Sequence only</SelectItem>
                        <SelectItem value="Lyrics only">Lyrics only</SelectItem>
                        <SelectItem value="Sequence & Lyrics">Sequence & Lyrics</SelectItem>
                        <SelectItem value="Singing Face">Singing Face</SelectItem>
                        <SelectItem value="Matrix Video">Matrix Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Media URLs */}
                <div className="space-y-4">
                  <Label className="text-base font-medium">Media URLs</Label>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="image_url">Image URL</Label>
                      <Input
                        id="image_url"
                        value={formData.image_url || ''}
                        onChange={(e) => handleFieldChange('image_url', e.target.value)}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="video_url">Video URL</Label>
                      <Input
                        id="video_url"
                        value={formData.video_url || ''}
                        onChange={(e) => handleFieldChange('video_url', e.target.value)}
                        placeholder="https://example.com/video.mp4"
                      />
                    </div>
                  </div>
                </div>

                {/* Layout Photos */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    <Label className="text-base font-medium">Layout Photos</Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Upload photos of the light display layout to help users visualize the sequence
                  </p>
                  {photosLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-sm text-muted-foreground">Loading photos...</p>
                    </div>
                  ) : (
                    <LayoutPhotoUpload
                      photos={layoutPhotos}
                      onPhotosChange={handleLayoutPhotosChange}
                      maxPhotos={3}
                      disabled={saving}
                    />
                  )}
                </div>

                {/* Spotify Data */}
                {spotifyData && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-800">Spotify Data Applied</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Album: {spotifyData.album.name}</p>
                      <p>Popularity: {spotifyData.popularity}/100</p>
                      <p>Duration: {Math.round(spotifyData.duration_ms / 1000)}s</p>
                      {spotifyData.explicit && <Badge variant="secondary">Explicit</Badge>}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* File Upload */}
            <FileUploadSection
              currentFileUrl={formData.file_url}
              onFileUpload={handleFileUpload}
              disabled={!selectedSequenceId && !isCreating}
            />

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name || !formData.title}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isCreating ? 'Create Sequence' : 'Update Sequence'}
                  </>
                )}
              </Button>
              {selectedSequenceId && !isCreating && (
                <Button
                  onClick={() => setShowDeleteDialog(true)}
                  variant="destructive"
                  disabled={deleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </div>

          {/* Right Side - Spotify Search */}
          <div>
            <SpotifySearchSection
              onTrackSelect={handleSpotifySelect}
              currentSequence={selectedSequence}
              defaultSearchTerm={formData.title}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sequence</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{formData.title || formData.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}